import React, { useEffect, useState } from 'react';
import { NextComponentType } from 'next';
import { AppContext, AppInitialProps, AppProps } from 'next/app';
import Head from 'next/head';
import CssBaseline from '@mui/material/CssBaseline';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { END } from 'redux-saga';
import { setLocale } from 'yup';
import { appWithTranslation } from 'i18n';
import { wrapper } from 'store/makeStore';
import { isServer } from 'utils/next';
import { routes } from 'utils/routing';
import locale from 'utils/locale';
import theme from 'theme';
import { Provider } from 'react-redux';
import { CacheProvider } from '@emotion/react';
import { TssCacheProvider } from 'tss-react';

import renderGtagScript from 'components/scripts/renderGtagScript';
import NotificationManager from 'components/common/NotificationManager';
import { ModalProvider } from 'components/common/ModalManager';
import { RollbarProvider } from 'components/common/Rollbar';
import { FirebaseProvider } from 'components/common/Firebase';
import { useExcludedScripts } from 'hooks/useExcludedScripts';
import createEmotionCache, { createTssCache } from 'utils/createEmotionCache';
import renderMixpanelScript from 'components/scripts/renderMixpanelScript';
import renderUserWayScript from 'components/scripts/renderUserWayScript';
import FacebookPixelSDK from 'components/scripts/renderFacebookPixelScript';
import useMedian from '../hooks/useMedian';
import useSpreedlyScript from '../components/scripts/renderSpreedlyScript';
import { useIncludedScripts } from '../hooks/useIncludedScripts';
import renderFacebookScript from '../components/scripts/renderFacebookScript';

setLocale(locale);
const clientSideEmotionCache = createEmotionCache();
const clientSideTssCache = createTssCache();

const excludedPaths = [routes.eventOrderPaymentMethod(0).href.pathname, routes.userPaymentMethods().href.pathname];
const includedPaths = [routes.eventOrderPaymentMethod(0).href.pathname, routes.userPaymentMethods().link];

const CustomApp: NextComponentType<AppContext, AppInitialProps, AppProps> = ({ Component, router, ...rest }) => {
  const { store, props } = wrapper.useWrappedStore(rest);
  const { emotionCache = clientSideEmotionCache, tssCache = clientSideTssCache, pageProps } = props;
  const [loaded, setLoaded] = useState(false);
  const { isMedian } = useMedian();
  const excluded = useExcludedScripts(router.pathname, excludedPaths);
  const included = useIncludedScripts(router.pathname, includedPaths);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useSpreedlyScript(included);

  return (
    <>
      <Head>
        <title>JLive</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width, user-scalable=0" />
        <meta
          name="apple-itunes-app"
          // eslint-disable-next-line max-len
          content="app-id=6736647530, affiliate-data=utm_source=jlivewebsite-header&utm_medium=banner&utm_campaign=ios_launch"
        />
        {excluded || renderGtagScript()}
        {excluded || renderFacebookScript()}
        {renderMixpanelScript()}
        {excluded || (loaded && !isMedian && renderUserWayScript())}
        {/* {included && renderSpreedlyScript()} */}
      </Head>
      {excluded || <FacebookPixelSDK />}
      <Provider store={store}>
        <CacheProvider value={emotionCache}>
          <TssCacheProvider value={tssCache}>
            <RollbarProvider>
              <FirebaseProvider disableInitialization={excluded}>
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <NotificationManager maxSnacks={3} />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <ModalProvider>
                        <Component {...pageProps} />
                      </ModalProvider>
                    </LocalizationProvider>
                  </ThemeProvider>
                </StyledEngineProvider>
              </FirebaseProvider>
            </RollbarProvider>
          </TssCacheProvider>
        </CacheProvider>
      </Provider>
    </>
  );
};

// todo change to getServerSide
CustomApp.getInitialProps = wrapper.getInitialAppProps((store) => async (appContext: AppContext) => {
  const { Component, ctx } = appContext;

  let pageProps = {};
  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps({ ...ctx, store });
  }

  if (isServer()) {
    store.dispatch(END);
    await store.sagaTask.toPromise();
  }

  return {
    pageProps,
  };
});

export default appWithTranslation(CustomApp);
